import { savedSearchPayloadSchema } from './types'

import type {
	PrettyfiedSearchParams,
	SavedSearchPayload,
	SearchParams,
} from './types'

/**
 * Prettyfy the search params to make them more readable and smaller for Tanstack Query keys
 * @param params - The search params to prettyfy
 */
export const prettyfySearchParams = (
	params: Partial<SearchParams>,
): PrettyfiedSearchParams => {
	const { int, ...rest } = params

	// We use sets to deduplicate the integrations and events
	const integrations = new Set<string>()
	const events = new Set<string>()

	let defaultParams: PrettyfiedSearchParams = {
		...rest,
		integrations: [],
		events: [],
	}

	if (params.int) {
		params.int.map(({ id, ev }) => {
			integrations.add(id)
			events.add(ev)
		})
	}

	defaultParams = {
		...defaultParams,
		integrations: Array.from(integrations),
		events: Array.from(events),
	}

	return defaultParams
}

/**
 * Encode a v2 saved-search payload as a JSON string for the BE's opaque
 * `search_string` column. The `version: 2` discriminator lets us safely
 * ignore legacy v1 rows.
 */
export function encodeSavedSearch(payload: SavedSearchPayload): string {
	return JSON.stringify(payload)
}

/**
 * Decode a saved-search payload, returning null for legacy v1 rows and
 * malformed JSON. The route's saved-search list filters these nulls out.
 */
export function decodeSavedSearch(
	searchString: string,
): SavedSearchPayload | null {
	try {
		const parsed = JSON.parse(searchString)
		const result = savedSearchPayloadSchema.safeParse(parsed)
		return result.success ? result.data : null
	} catch {
		return null
	}
}

/**
 * Whether the current URL state is complete enough to save.
 *
 * A saved search with no `tr` throws on load, and one with no `int` loads with
 * nothing selected — the Log Source auto-fill is one-shot per mount
 * (`use-integration-select.ts`), so it never re-arms to repair it. Both are
 * cheap to refuse at save time and awkward to recover from at load time.
 */
export function canSaveSearch(
	search: Pick<SearchParams, 'int' | 'tr'>,
): boolean {
	return !!search.tr && !!search.int?.length
}

/**
 * The URL state a saved search loads into, given what is on screen now.
 *
 * Rows saved before `canSaveSearch` existed can be missing `tr` or `int`:
 * - no `tr` → stays `undefined`, and the route's `beforeLoad` fills the default
 *   window (`ensureSearchTimeRange`), which is the one owner of that default.
 * - no `int` → the current selection is kept, because an empty Log Source
 *   searches nothing and the auto-fill will not re-arm to fix it.
 */
export function resolveSavedSearchParams(
	payload: SavedSearchPayload,
	current: Pick<SearchParams, 'int'>,
): Pick<SearchParams, 'int' | 'tr' | 'q' | 'ft'> {
	return {
		int: payload.int?.length ? payload.int : current.int,
		tr: payload.tr,
		q: payload.q,
		ft: payload.ft,
	}
}
